import React from 'react'
import Link from 'next/link'

/**
 * Freelance vs gym-employed comparison for the freelance trainer page.
 *
 * The female and freelance pages previously shared ~93 of 130 long sentences.
 * This is page-specific substance aimed at the actual question behind the query:
 * someone deciding whether to hire an independent trainer or sign up at a gym.
 *
 * Deliberately even-handed - it says plainly where a gym is the better choice.
 * A comparison that never concedes anything reads as marketing and converts worse.
 */

const rows = [
    {
        factor: 'Where you train',
        freelance: 'Your home, condo or HDB gym, or a park nearby',
        gym: 'The gym floor, during its opening hours',
    },
    {
        factor: 'Membership fee',
        freelance: 'None - you pay for sessions only',
        gym: 'Membership on top of session fees',
    },
    {
        factor: 'Equipment access',
        freelance: 'What you have, plus what the trainer brings',
        gym: 'Full range of machines and free weights',
    },
    {
        factor: 'Scheduling',
        freelance: 'Set directly with your trainer, including early or late slots',
        gym: 'Limited to staffed hours and trainer availability',
    },
    {
        factor: 'Travel time',
        freelance: 'None - the trainer comes to you',
        gym: 'Both ways, every session',
    },
    {
        factor: 'Continuity',
        freelance: 'You keep the same trainer as long as you both want to',
        gym: 'Trainer turnover can reassign you mid-package',
    },
]

export const FreelanceTrainerComparison = () => (
    <section className='relative bg-slate-900 py-12 md:py-16 overflow-hidden'>
        <div className='absolute top-0 left-0 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl pointer-events-none' />

        <div className='relative z-10 max-w-5xl mx-auto px-4'>
            <div className='text-center mb-8'>
                <div className='inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 border border-white/10'>
                    <span className='text-sm'>⚖️</span>
                    <span className='text-xs font-medium text-white/70'>Comparison</span>
                </div>
                <h2 className='text-2xl md:text-3xl font-extrabold text-white tracking-tight'>
                    Freelance Personal Trainer or Gym Trainer?
                </h2>
                <p className='text-sm md:text-base text-white/60 mt-3 max-w-2xl mx-auto leading-relaxed'>
                    The honest trade-offs between hiring an independent trainer in Singapore and
                    signing up for personal training at a commercial gym.
                </p>
            </div>

            <div className='overflow-x-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md'>
                <table className='w-full text-xs md:text-sm border-collapse'>
                    <caption className='sr-only'>
                        Freelance personal trainer compared with gym-employed personal trainer in Singapore
                    </caption>
                    <thead>
                        <tr className='bg-linear-to-r from-orange-500 to-amber-500 text-white'>
                            <th scope='col' className='py-2.5 px-3 md:px-4 text-left font-semibold'>Factor</th>
                            <th scope='col' className='py-2.5 px-3 md:px-4 text-left font-semibold'>Freelance trainer</th>
                            <th scope='col' className='py-2.5 px-3 md:px-4 text-left font-semibold'>Gym trainer</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={row.factor} className={`${i % 2 === 0 ? 'bg-white/5' : 'bg-white/2'} border-b border-white/5`}>
                                <th scope='row' className='py-2.5 px-3 md:px-4 font-medium text-white/80 text-left'>{row.factor}</th>
                                <td className='py-2.5 px-3 md:px-4 text-white/70'>{row.freelance}</td>
                                <td className='py-2.5 px-3 md:px-4 text-white/50'>{row.gym}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='mt-8 grid md:grid-cols-2 gap-4'>
                <div className='bg-white/5 border border-white/10 rounded-2xl p-5'>
                    <h3 className='text-base font-bold text-white mb-2'>A freelance trainer suits you if</h3>
                    <p className='text-sm leading-relaxed text-white/65'>
                        Your schedule is tight, you have a condo or block gym downstairs, you would rather not
                        train on a crowded floor, or you want to avoid paying a membership on top of session fees.
                    </p>
                </div>
                <div className='bg-white/5 border border-white/10 rounded-2xl p-5'>
                    <h3 className='text-base font-bold text-white mb-2'>A gym is the better choice if</h3>
                    <p className='text-sm leading-relaxed text-white/65'>
                        You want heavy barbell work, machines and specialist equipment, you have no usable space
                        at home, or you actively want to train around other people for the atmosphere.
                    </p>
                </div>
            </div>

            <p className='text-sm text-white/60 mt-8 text-center leading-relaxed max-w-3xl mx-auto'>
                Still weighing it up on price?{' '}
                <Link href='/sg/personal-trainer/personal-trainer-rates-singapore' prefetch={false}
                    className='text-orange-400 hover:text-orange-300 underline'>
                    Compare what personal trainers charge across Singapore
                </Link>
                {' '}by experience level, training location and package size.
            </p>
        </div>
    </section>
)
